import { useState } from "react";
import {
  useDeleteRestaurantRequestMutation,
  useFetchRestaurantRequestsQuery,
} from "../store";
import Pagination from "../components/common/Pagination.tsx";
import { FaRegTrashAlt } from "react-icons/fa";
import TextField from "../components/common/TextField.tsx";
import useDebounce from "../hooks/useDebounce.tsx";
import {
  ApplicantRoleLabels,
  RestaurantRequest,
  WeeklyOrdersLabels,
} from "../types";
import { showToast, ToastType } from "../utils/toast.utils.ts";
import Modal from "../components/common/Modal.tsx";
import Button from "../components/common/Button.tsx";

const RestaurantApplications: React.FC = () => {
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(20);
  const [displayName, setName] = useState<string>("");
  const debouncedDisplayName = useDebounce(displayName, 400);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [requestToDelete, setRequestToDelete] =
    useState<RestaurantRequest | null>(null);

  const [deleteRestaurantRequest] = useDeleteRestaurantRequestMutation();

  const confirmDelete = (requestToDelete: RestaurantRequest) => {
    setRequestToDelete(requestToDelete);
    setShowConfirmModal(true);
  };

  const handleDeleteRequest = async () => {
    if (!requestToDelete) return;
    const result = await deleteRestaurantRequest(requestToDelete._id);
    if (!result.error) {
      showToast("Demande supprimée", ToastType.SUCCESS);
    }
    setShowConfirmModal(false);
    setRequestToDelete(null);
  };

  const { data: requests } = useFetchRestaurantRequestsQuery({
    page,
    perPage,
    displayName: debouncedDisplayName,
  });

  return (
    <div className="p-6">
      <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Candidatures Restaurants
          </h2>
          <div className="w-full sm:w-72">
            <TextField
              id="displayName"
              value={displayName}
              onChange={(e) => setName((e.target as HTMLInputElement).value)}
              placeholder="Rechercher un restaurant..."
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Restaurant
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Localisation
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rôle
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Commandes/sem
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {requests?.data?.map((row) => (
                <tr
                  key={row._id}
                  className="hover:bg-gray-50 transition-colors"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {row.displayName}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{row.city}</div>
                    <div className="text-xs text-gray-500">{row.address}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {row.firstName} {row.lastName}
                    </div>
                    <div className="text-xs text-gray-500">{row.email}</div>
                    <div className="text-xs text-gray-500">{row.phone}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                      {ApplicantRoleLabels[row.role]}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {WeeklyOrdersLabels[row.weeklyOrders]}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {row.createdAt}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => confirmDelete(row)}
                      className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                      title="Supprimer"
                    >
                      <FaRegTrashAlt className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="border-t border-gray-200 p-4">
          <Pagination
            paginationInfo={{
              page,
              perPage,
              count: requests?.paginationInfo.count || 1,
            }}
            setPage={setPage}
            setPerPage={setPerPage}
          />
        </div>
      </div>

      {showConfirmModal && (
        <Modal
          onClose={() => setShowConfirmModal(false)}
          actionBar={
            <>
              <Button
                className="bg-gray-100 text-gray-700 hover:bg-gray-200 px-4 py-2 rounded mr-2"
                onClick={() => setShowConfirmModal(false)}
              >
                Annuler
              </Button>
              <Button
                className="bg-red-600 text-white hover:bg-red-700 px-4 py-2 rounded"
                onClick={handleDeleteRequest}
              >
                Supprimer
              </Button>
            </>
          }
        >
          <div className="text-center">
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Confirmer la suppression
            </h3>
            <p className="text-sm text-gray-500">
              Êtes-vous sûr de vouloir supprimer la demande de{" "}
              <span className="font-semibold text-gray-900">
                {requestToDelete?.firstName} {requestToDelete?.lastName}
              </span>{" "}
              ? Cette action est irréversible.
            </p>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default RestaurantApplications;
